import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  makeStyles,
  Typography
} from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import DeleteIcon from "@material-ui/icons/Delete";
import CardTitle from "@saleor/components/CardTitle";
import Grid from "@saleor/components/Grid";
import Hr from "@saleor/components/Hr";
import { FormChange } from "@saleor/hooks/useForm";
import useNotifier from "@saleor/hooks/useNotifier";
import ImageIcon from "@saleor/icons/Image";
import { buttonMessages } from "@saleor/intl";
import classNames from "classnames";
import React from "react";
import Dropzone from "react-dropzone";
import { FormattedMessage, useIntl } from "react-intl";

import { StoreCreatePageFormData } from "../StoreCreatePage";

interface StoreBannerImageProps {
  data: StoreCreatePageFormData;
  loading?: boolean;
  onChange: FormChange;
  onImageUpload: (file: File) => void;
  hasManageTenantPermission: boolean;
}

const StoreBannerImage: React.FC<StoreBannerImageProps> = props => {
  const {
    data,
    loading,
    onChange,
    onImageUpload,
    hasManageTenantPermission
  } = props;

  const useStyles = makeStyles(
    theme => ({
      card: {
        marginTop: theme.spacing(2),
        [theme.breakpoints.down("sm")]: {
          marginTop: 0
        }
      },
      cardContentDisabled: {
        opacity: "0.6",
        pointerEvents: "none"
      },
      descriptionLabel: {
        margin: "5px 0px 5px 0px"
      },
      fileField: {
        display: "none"
      },
      fileUploadField: {
        padding: "0!important"
      },
      guidelines: {
        fontWeight: "bold"
      },
      helpText: {
        gridColumnEnd: "span 4"
      },
      icon: {
        color: "rgba(255, 255, 255, 0.54)"
      },
      image: {
        height: "100%",
        objectFit: "contain",
        userSelect: "none",
        width: "100%"
      },
      imageContainer: {
        "&:hover, &.dragged": {
          "& $imageOverlay": {
            display: "block"
          }
        },
        background: "#ffffff",
        border: "1px solid #eaeaea",
        borderRadius: theme.spacing(),
        height: 140,
        margin: "auto",
        overflow: "hidden",
        position: "relative",
        width: 140
      },
      imageGridContainer: {
        position: "relative"
      },

      imageOverlay: {
        background: "rgba(0, 0, 0, 0.6)",
        cursor: "move",
        display: "none",
        height: "100%",
        left: 0,
        position: "absolute",
        top: 0,
        width: "100%"
      },
      imageOverlayShow: {
        "&$imageOverlay": {
          alignItems: "center",
          display: "flex",
          justifyContent: "center"
        }
      },
      imageOverlayToolbar: {
        display: "flex",
        position: "absolute",
        right: 0,
        top: 0
      },
      imageUpload: {
        height: "100%",
        left: 0,
        outline: 0,
        position: "absolute",
        top: 0,
        width: "100%"
      },
      imageUploadActive: {
        zIndex: 1
      },
      imageUploadIconActive: {
        display: "block"
      },
      info: {
        color: "#0937f4",
        fontSize: 17,
        fontWeight: "bold"
      },
      infoGuidelines: {
        fontSize: 15,
        marginLeft: "10px"
      },
      photosIcon: {
        height: "64px",
        margin: "0 auto",
        width: "64px"
      },
      root: {
        display: "grid",
        gridColumnGap: theme.spacing(2),
        gridRowGap: theme.spacing(2),
        gridTemplateColumns: "repeat(4, 1fr)",
        [theme.breakpoints.down("sm")]: {
          gridTemplateColumns: "repeat(3, 1fr)"
        },
        [theme.breakpoints.down("xs")]: {
          gridTemplateColumns: "repeat(2, 1fr)"
        }
      },
      rootDragActive: {
        opacity: 0.2
      },
      uploadButton: {
        display: "block"
      },
      uploadRoot: {
        fontSize: 12,
        fontWeight: 600,
        padding: "15px",
        textAlign: "center"
      }
    }),
    { name: "StoreBannerImage" }
  );

  const notify = useNotifier();
  const classes = useStyles(props);

  const intl = useIntl();
  const upload = React.useRef(null);
  const [imageToUpload, setImageToUpload] = React.useState(null);

  React.useEffect(() => {
    if (imageToUpload) {
      onImageUpload(imageToUpload[0]);
    } else {
      onImageUpload(null);
    }
  }, [imageToUpload]);

  const fileSizeLimit = 1.5e6;

  const checkFile = e => {
    const file = e[0];
    if (
      file.type.includes("image/jpeg") ||
      file.type.includes("image/jpg") ||
      file.type.includes("image/png")
    ) {
      if (file.size < fileSizeLimit) {
        setImageToUpload(e);
        onImageUpload(e);
        onChange({
          target: {
            name: "bannerImage",
            value: file
          }
        });
      } else {
        notify({
          status: "error",
          text: "File size is too big! Try compressing.",
          title: "Can't Upload."
        });
      }
    } else {
      notify({
        status: "error",
        text: "File not supported"
      });
    }
  };

  return (
    <Card className={classes.card}>
      <CardTitle
        title={intl.formatMessage({
          defaultMessage: "Store Banner Image",
          description: "section header"
        })}
        toolbar={
          <>
            <Button
              id="uploadImageBtn"
              onClick={() => upload.current.click()}
              disabled={loading || !hasManageTenantPermission}
              variant="text"
              color="primary"
              data-test="button-upload-image"
              className={classes.uploadButton}
            >
              {intl.formatMessage(buttonMessages.uploadImage)}
            </Button>
            <input
              className={classes.fileField}
              id="fileUpload"
              onChange={event => checkFile(event.target.files)}
              type="file"
              ref={upload}
              accept="image/jpeg, image/jpg, image/png"
            />
          </>
        }
      />
      <div className={classes.imageGridContainer}>
        <CardContent
          className={
            hasManageTenantPermission ? "" : classes.cardContentDisabled
          }
        >
          <Dropzone onDrop={checkFile} disableClick={true}>
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps()}>
                <CardContent
                  className={
                    hasManageTenantPermission
                      ? classes.fileUploadField
                      : `${classes.cardContentDisabled} ${classes.fileUploadField}`
                  }
                >
                  <input
                    {...getInputProps()}
                    id="fileUpload"
                    accept="image/jpeg, image/jpg, image/png"
                    onChange={e =>
                      e.target.files.length > 0 && checkFile(e.target.files)
                    }
                    multiple={false}
                    ref={upload}
                    type="file"
                  />
                  {imageToUpload || data.bannerImage?.url ? (
                    <div className={classes.imageContainer}>
                      <div
                        className={classNames(classes.imageOverlay, {
                          [classes.imageOverlayShow]: loading
                        })}
                      >
                        {loading ? (
                          <CircularProgress size={32} />
                        ) : (
                          <div className={classes.imageOverlayToolbar}>
                            <IconButton
                              id="deleteImageBtn"
                              color="primary"
                              onClick={() => {
                                upload.current.value = null;
                                setImageToUpload(null);
                                onChange({
                                  target: {
                                    name: "bannerImage",
                                    value: null
                                  }
                                });
                              }}
                              disabled={!hasManageTenantPermission}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </div>
                        )}
                      </div>
                      <img
                        className={classes.image}
                        src={
                          imageToUpload
                            ? URL.createObjectURL(imageToUpload[0])
                            : data.bannerImage.url
                        }
                      />
                    </div>
                  ) : (
                    <div className={classes.uploadRoot}>
                      <ImageIcon className={classes.photosIcon} />
                      <p className={classes.descriptionLabel}>
                        <FormattedMessage
                          defaultMessage="DROP IMAGE HERE TO UPLOAD"
                          description="description"
                        />
                      </p>
                    </div>
                  )}
                </CardContent>
              </div>
            )}
          </Dropzone>
        </CardContent>
        <Hr />
        <CardContent
          className={
            hasManageTenantPermission ? "" : classes.cardContentDisabled
          }
        >
          <Grid>
            <div
              style={{
                alignItems: "center",
                display: "flex",
                flexWrap: "wrap"
              }}
            >
              <InfoOutlined color="primary" />
              <Typography className={classes.info}>
                {" "}
                Upload Guidelines:
              </Typography>
            </div>
          </Grid>
          <div>
            <span className={classes.infoGuidelines}>
              {" "}
              1. Image file should{" "}
              <span className={classes.guidelines}>
                not be greater than 1.5MB.
              </span>
            </span>
            <br />
            <span className={classes.infoGuidelines}>
              {" "}
              2. Only{" "}
              <span className={classes.guidelines}>
                JPG, JPEG and PNG files
              </span>{" "}
              are acceptable
            </span>
          </div>
        </CardContent>
      </div>
    </Card>
  );
};
StoreBannerImage.displayName = "StoreBannerImage";
export default StoreBannerImage;
